import { createContext, useContext, useEffect, useState } from 'react'

import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'

type ResolvedMode = 'dark' | 'light'
type Mode = ResolvedMode | 'system'

const DEFAULT_MODE: Mode = 'system'

type ModeProviderProps = {
  children: React.ReactNode
  defaultMode?: Mode
  storageKey?: string
}

type ModeProviderState = {
  mode: Mode
  resolvedMode: ResolvedMode
  setMode: (mode: Mode) => void
}

const getSystemMode = (): ResolvedMode =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

const resolveMode = (mode: Mode): ResolvedMode =>
  mode === 'system' ? getSystemMode() : mode

const initialState: ModeProviderState = {
  mode: DEFAULT_MODE,
  resolvedMode: 'light',
  setMode: () => null,
}

const ModeProviderContext = createContext<ModeProviderState>(initialState)

export const ModeProvider = ({
  children,
  defaultMode = DEFAULT_MODE,
  storageKey = 'praxis-ui-mode',
  ...props
}: ModeProviderProps) => {
  const [mode, setMode] = useState<Mode>(() => {
    const stored = localStorage.getItem(storageKey)

    if (stored === 'dark' || stored === 'light' || stored === 'system') {
      return stored
    }

    return defaultMode
  })

  const { isFeatureEnabled: isDarkModeEnabled } = useFeatureFlag('dark_mode')

  const [resolvedMode, setResolvedMode] = useState<ResolvedMode>(() =>
    isDarkModeEnabled ? resolveMode(mode) : 'light'
  )

  useEffect(() => {
    const root = window.document.documentElement
    const applied = isDarkModeEnabled ? resolveMode(mode) : 'light'

    setResolvedMode(applied)
    root.classList.remove('light', 'dark')
    root.classList.add(applied)

    if (!isDarkModeEnabled || mode !== 'system') {
      return
    }

    const query = window.matchMedia('(prefers-color-scheme: dark)')

    const applySystemMode = () => {
      const systemMode = getSystemMode()

      setResolvedMode(systemMode)
      root.classList.remove('light', 'dark')
      root.classList.add(systemMode)
    }

    query.addEventListener('change', applySystemMode)

    return () => query.removeEventListener('change', applySystemMode)
  }, [mode, isDarkModeEnabled])

  const value = {
    mode,
    resolvedMode,
    setMode: (mode: Mode) => {
      localStorage.setItem(storageKey, mode)
      setMode(mode)
    },
  }

  return (
    <ModeProviderContext.Provider {...props} value={value}>
      {children}
    </ModeProviderContext.Provider>
  )
}

export const useMode = () => {
  const context = useContext(ModeProviderContext)

  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider')
  }

  return context
}
